/**
 * Custom hook for message handling operations
 * Encapsulates submit and regenerate logic
 */
import { useCallback, type FormEvent } from "react";
import type { PromptInputMessage } from "@/components/ai-elements/prompt-input";
import { buildClientDateTimeContext } from "@/shared/client-date-time-context";
import { getModelConfig } from "@/shared/models";
import type { DebugTraceEvent } from "@/shared/types/debug-trace";
import type { ChatUIMessage } from "@/shared/types/chat-ui-message";

/**
 * Type for the sendMessage function from useAgentChat
 * Using a generic function signature that matches the library
 */
interface SendMessageInput {
  role: "user";
  parts: ChatUIMessage["parts"];
}

interface SendMessageOptions {
  body: Record<string, unknown>;
}

type SendMessageFunction = (
  message: SendMessageInput,
  options?: SendMessageOptions
) => Promise<void>;

interface UseMessageHandlingProps {
  /** Function to send a message to the agent */
  sendMessage: SendMessageFunction;
  /** Current selected model ID */
  model: string;
  /** Array of agent messages */
  messages: ChatUIMessage[];
  /** Optional callback for debug trace events */
  onDebugEvent?: (event: Partial<DebugTraceEvent>) => void;
}

interface UseMessageHandlingReturn {
  /** Handler for submitting a new message */
  handleSubmit: (
    message: PromptInputMessage,
    event?: FormEvent<HTMLFormElement>
  ) => Promise<void>;
  /** Handler for regenerating the last response */
  regenerate: () => Promise<void>;
}

/**
 * Hook that provides message handling operations
 * Centralizes the logic for submitting messages and regenerating responses
 *
 * @example
 * ```tsx
 * const { handleSubmit, regenerate } = useMessageHandling({
 *   sendMessage,
 *   model,
 *   messages: agentMessages
 * });
 *
 * <ChatInput onSubmit={handleSubmit} />
 * <MessageList onRegenerate={regenerate} />
 * ```
 */
export function useMessageHandling({
  sendMessage,
  model,
  messages,
  onDebugEvent
}: UseMessageHandlingProps): UseMessageHandlingReturn {
  const handleSubmit = useCallback(
    async (message: PromptInputMessage, event?: FormEvent<HTMLFormElement>) => {
      const hasText = Boolean(message.text);
      const hasAttachments = Boolean(message.files?.length);

      if (!(hasText || hasAttachments)) {
        return;
      }

      // Clear the input immediately upon valid submit (only if event exists)
      event?.currentTarget?.reset();

      const modelConfig = getModelConfig(model);
      onDebugEvent?.({
        source: "client",
        category: "chat",
        level: "info",
        title: "User message submitted",
        payload: {
          hasText,
          attachmentCount: message.files?.length || 0,
          selectedModelId: model,
          reasoningEffort: modelConfig?.reasoningEffort
        }
      });
      // Send message to agent using AI Elements message format
      await sendMessage(
        {
          role: "user",
          parts: [
            {
              type: "data-clientDateTimeContext",
              data: buildClientDateTimeContext()
            },
            {
              type: "text",
              text: message.text || "Sent with attachments"
            },
            // Handle file attachments if present
            ...(message.files || []).map((file) => file)
          ]
        },
        {
          body: {
            selectedModelId: model,
            reasoningEffort: modelConfig?.reasoningEffort
          }
        }
      );
    },
    [sendMessage, model, onDebugEvent]
  );

  const regenerate = useCallback(async () => {
    // Find the last user message and resend it
    const lastUserMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.role === "user");

    if (lastUserMessage) {
      const modelConfig = getModelConfig(model);
      const refreshedParts = (lastUserMessage.parts || []).map((part) =>
        part.type === "data-clientDateTimeContext"
          ? {
              type: "data-clientDateTimeContext" as const,
              data: buildClientDateTimeContext()
            }
          : part
      );
      onDebugEvent?.({
        source: "client",
        category: "chat",
        level: "info",
        title: "Regenerate triggered",
        payload: {
          selectedModelId: model,
          reasoningEffort: modelConfig?.reasoningEffort,
          partCount: lastUserMessage.parts?.length || 0
        }
      });
      // Create a new message object without the old ID to avoid duplicate keys
      try {
        await sendMessage(
          {
            role: "user",
            parts: refreshedParts
          },
          {
            body: {
              selectedModelId: model,
              reasoningEffort: modelConfig?.reasoningEffort
            }
          }
        );
      } catch (error) {
        onDebugEvent?.({
          source: "client",
          category: "chat",
          level: "error",
          title: "Regenerate failed",
          payload: {
            selectedModelId: model,
            error: error instanceof Error ? error.message : String(error)
          }
        });
      }
    }
  }, [messages, sendMessage, model, onDebugEvent]);

  return {
    handleSubmit,
    regenerate
  };
}
